import * as React from 'react';
import _ from 'lodash';

import { Button, Typography } from 'omakase';
import { IconArrowDownSmall, IconArrowUpSmall } from 'ui/icons';
import { IconChevronSmallDown, IconChevronSmallUp } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Menu, MenuItem, Tab, TabSize, Tabs } from 'ui';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => {
  return {
    showMobile: {
      display: 'block',
      [theme.breakpoints.up('sm')]: { display: 'none' },
    },
    hideMobile: {
      display: 'none',
      [theme.breakpoints.up('sm')]: { display: 'block' },
    },
    menu: {
      '& .MuiPaper-root': {
        boxShadow: theme.shadow[100],
        border: `1px solid ${theme.colors.borderDefault}`,
      },
      '& .MuiList-root': {
        minWidth: 250,
        paddingBottom: 0,
        paddingTop: 0,
      },
      '& .MuiListItem-root': {
        minHeight: theme.spacing(4),
        padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
        '&.Mui-selected, &.Mui-selected:hover': {
          background: 'inherit',
          color: theme.colors.fgPrimaryBase,
        },
      },
      '& .MuiListItem-root svg': {
        height: 20,
        marginRight: theme.spacing(1),
        width: 20,
      },
    },
    menuButton: {
      border: 'none',
      borderBottom: '2px solid',
      borderRadius: 0,
      color: theme.colors.fgPrimaryBase,
      justifyContent: 'flex-start',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '& .MuiTypography-root': {
        flex: 1,
      },
      '& .MuiButton-startIcon > *:first-child': { fontSize: 24 },
      '&.open': {
        borderColor: theme.colors.borderDefault,
        color: theme.colors.fgSubtle,
      },
    },
    menuButtonXLarge: {
      minHeight: theme.spacing(8),
    },
    menuButtonLarge: {
      minHeight: theme.spacing(6),
    },
  };
});

export type TabValue = number | string;
export interface TabData<T> {
  icon?: React.ReactElement;
  label?: string;
  text?: string;
  value: T;
}

export interface DynamicTabsProps<T> {
  className?: string;
  onChange?: (value: T) => void;
  size?: TabSize;
  tabs: TabData<T>[];
  value?: T;
  variant?: 'menu' | 'menuMobile' | 'tabs';
}

export function DynamicTabs<T = 'string'>({
  className = '',
  onChange = _.noop,
  size = 'large',
  tabs,
  value,
  variant = 'tabs',
}: DynamicTabsProps<T>) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [anchor, setAnchor] = React.useState<undefined | HTMLButtonElement>();
  const classes = useStyles();

  const onMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuOpen(!menuOpen);
    setAnchor(event.currentTarget);
  };
  const handleTabChange = (_e?: React.ChangeEvent<{}>, value?: T): void => {
    if (value === undefined) {
      return;
    }
    onChange(value);
  };

  const activeTab = tabs.find((tab) => tab.value === value);

  const renderItemText = (text: string) => (
    <Typography variant="body02Strong" color="inherit" component="span">
      {text}
    </Typography>
  );

  const ariaAttributes = ({ icon, label, text }: TabData<T>) => {
    if (icon || !!label) {
      return { 'aria-label': label || text };
    }
    return {};
  };

  const menuOffset = size === 'large' ? -4 : -8;

  const renderMenu = () => (
    <>
      <Button
        className={clsx(classes.menuButton, {
          open: menuOpen,
        })}
        iconEnd={
          menuOpen ? (
            <IconMigrationUtil legacyIcon={<IconArrowUpSmall />} newIcon={<IconChevronSmallUp />} />
          ) : (
            <IconMigrationUtil
              legacyIcon={<IconArrowDownSmall />}
              newIcon={<IconChevronSmallDown />}
            />
          )
        }
        size={size}
        fullWidth
        onClick={onMenuOpen}
        iconStart={activeTab?.icon}
        variant="basic"
      >
        {activeTab?.text}
      </Button>
      <Menu
        anchorEl={anchor}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        getContentAnchorEl={null}
        keepMounted
        className={classes.menu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: menuOffset, horizontal: menuOffset }}
        MenuListProps={{ variant: 'selectedMenu' }}
      >
        {tabs.map((data: TabData<T>) => (
          <MenuItem
            selected={data.value === value}
            key={`${data.value}-${data.text}`}
            {...ariaAttributes(data)}
            onClick={() => {
              if (data.value) {
                onChange(data.value);
              }
              setMenuOpen(false);
            }}
          >
            {data.icon}
            {data.text && renderItemText(data.text)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const renderTabs = () => (
    <Tabs
      contained
      onChange={handleTabChange}
      scrollButtons="off"
      variant="scrollable"
      value={value}
    >
      {tabs.map((data: TabData<T>) => (
        <Tab
          icon={data.icon}
          size={size}
          value={data.value}
          label={data.text && renderItemText(data.text)}
          {...ariaAttributes(data)}
          key={`${data.value}-${data.text}`}
        />
      ))}
    </Tabs>
  );

  if (variant === 'menu') {
    return <div className={className}>{renderMenu()}</div>;
  } else if (variant === 'tabs') {
    return <div className={className}>{renderTabs()}</div>;
  }
  return (
    <>
      <div className={clsx(className, classes.showMobile)}>{renderMenu()}</div>
      <div className={clsx(className, classes.hideMobile)}>{renderTabs()}</div>
    </>
  );
}
